
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle } from 'lucide-react';

const ApprovalPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing your approval request...');

  useEffect(() => {
    const processApproval = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid approval link. No token provided.');
        return;
      }

      try {
        const response = await fetch(`https://rrsgsfpxfwcnlttxnlom.supabase.co/functions/v1/approve-access?token=${token}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to process approval');
        }

        setStatus('success');
        setMessage('Access has been successfully granted!');
      } catch (error: any) {
        console.error('Error processing approval:', error);
        setStatus('error');
        setMessage(error.message || 'Failed to process approval. Please try again.');
      }
    };

    processApproval();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE] flex items-center justify-center p-6">
      <Card className="w-full max-w-lg border border-[#0EA5E9]/20 shadow-md bg-white/80">
        <CardHeader>
          <CardTitle className="flex items-center justify-center gap-2 text-center">
            {status === 'loading' && 'Processing Approval'}
            {status === 'success' && 'Approval Successful'}
            {status === 'error' && 'Approval Failed'}
          </CardTitle>
          <CardDescription className="text-center">
            Talento AI Access Management
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col items-center justify-center text-center">
            {status === 'loading' && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 rounded-full border-4 border-[#0EA5E9] border-t-transparent animate-spin"></div>
                <p>{message}</p>
              </div>
            )}
            
            {status === 'success' && (
              <div className="flex flex-col items-center gap-4">
                <CheckCircle className="w-16 h-16 text-green-500" />
                <p className="text-lg">{message}</p>
                <p className="text-gray-600">The user can now access the requested feature.</p>
              </div>
            )}
            
            {status === 'error' && (
              <div className="flex flex-col items-center gap-4">
                <XCircle className="w-16 h-16 text-red-500" />
                <p className="text-lg">{message}</p>
                <p className="text-gray-600">Please check the approval link and try again.</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-center">
            <Link to="/">
              <Button className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90">
                Return to Home
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApprovalPage;
